(() => {
  'use strict';
  const instances = new WeakMap();

  function slug(value) {
    return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function copyText(value) {
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(value);
    return new Promise((resolve, reject) => {
      const active = document.activeElement;
      const field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
      document.body.appendChild(field);
      field.select();
      try {
        if (document.execCommand('copy')) resolve();
        else reject(new Error('Copy failed'));
      } catch (error) { reject(error); }
      field.remove();
      if (active instanceof HTMLElement) active.focus({ preventScroll: true });
    });
  }

  function init(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const prose = root.querySelector('[data-nnfa-prose]');
    const toc = root.querySelector('[data-nnfa-toc]');
    const tocList = root.querySelector('[data-nnfa-toc-list]');
    const progress = root.querySelector('[data-nnfa-progress]');
    const status = root.querySelector('[data-nnfa-share-status]');
    let observer;
    let frame;
    let statusTimer;

    if (prose && toc && tocList) {
      const headings = [...prose.querySelectorAll('h2,h3')].filter(heading => heading.textContent.trim());
      const ids = new Map();
      document.querySelectorAll('[id]').forEach(element => ids.set(element.id, (ids.get(element.id) || 0) + 1));
      tocList.replaceChildren();
      headings.forEach((heading, index) => {
        if (!heading.id || ids.get(heading.id) !== 1) {
          const base = slug(heading.textContent) || `section-${index + 1}`;
          let id = base;
          let suffix = 2;
          while (ids.has(id)) id = `${base}-${suffix++}`;
          heading.id = id;
          ids.set(id, 1);
        }
        if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;
        const item = document.createElement('li');
        if (heading.tagName === 'H3') item.className = 'is-subsection';
        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(heading.id)}`;
        link.textContent = heading.textContent.trim();
        link.addEventListener('click', () => heading.focus({ preventScroll: true }), { signal });
        item.appendChild(link);
        tocList.appendChild(item);
      });
      toc.hidden = headings.length === 0;
      if (window.matchMedia('(max-width: 980px)').matches) toc.open = false;
      if (headings.length && 'IntersectionObserver' in window) {
        const links = [...tocList.querySelectorAll('a')];
        observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            links.forEach(link => {
              const current = link.hash === `#${encodeURIComponent(entry.target.id)}`;
              link.classList.toggle('is-active', current);
              if (current) link.setAttribute('aria-current', 'location');
              else link.removeAttribute('aria-current');
            });
          });
        }, { rootMargin: '-18% 0px -65% 0px', threshold: 0 });
        headings.forEach(heading => observer.observe(heading));
      }
      if (window.location.hash) {
        try {
          const target = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
          if (target && prose.contains(target)) requestAnimationFrame(() => target.scrollIntoView());
        } catch (error) {}
      }
      prose.querySelectorAll('table').forEach(table => {
        if (table.parentElement.classList.contains('nnfa-table')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'nnfa-table';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', table.caption?.textContent.trim() || root.dataset.tableLabel);
        table.before(wrapper);
        wrapper.appendChild(table);
      });
    }

    if (progress && prose) {
      const updateProgress = () => {
        frame = null;
        const rect = prose.getBoundingClientRect();
        const distance = Math.max(1, rect.height - window.innerHeight + 170);
        const percent = Math.max(0, Math.min(1, (170 - rect.top) / distance));
        progress.style.width = `${(percent * 100).toFixed(2)}%`;
      };
      const requestProgress = () => {
        if (!frame) frame = requestAnimationFrame(updateProgress);
      };
      updateProgress();
      window.addEventListener('scroll', requestProgress, { passive: true, signal });
      window.addEventListener('resize', requestProgress, { signal });
      window.addEventListener('load', requestProgress, { once: true, signal });
    }

    const announce = message => {
      if (!status) return;
      clearTimeout(statusTimer);
      status.textContent = '';
      statusTimer = setTimeout(() => { status.textContent = message; }, 30);
    };
    root.addEventListener('click', async event => {
      const formula = event.target.closest('.nnj-formula-bridge a');
      try {
        if (formula && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true) {
          window.NNPostHog?.capture?.('field_notes_product_open', {
            article_path: window.location.pathname,
            product_path: new URL(formula.href, window.location.origin).pathname
          });
        }
      } catch (error) {}
      const share = event.target.closest('[data-nnfa-share]');
      const button = share || event.target.closest('[data-nnfa-copy]');
      if (!button) return;
      const url = button.dataset.shareUrl || window.location.href;
      if (share && navigator.share) {
        try {
          await navigator.share({ title: button.dataset.shareTitle || document.title, url });
          announce(root.dataset.shareOpened);
          return;
        } catch (error) {
          if (error.name === 'AbortError') return;
        }
      }
      try {
        await copyText(url);
        announce(root.dataset.shareCopied);
      } catch (error) { announce(root.dataset.shareFailed); }
    }, { signal });

    instances.set(root, () => {
      controller.abort();
      observer?.disconnect();
      cancelAnimationFrame(frame);
      clearTimeout(statusTimer);
      instances.delete(root);
    });
  }

  const initAll = scope => scope.querySelectorAll('[data-nnfa-article]').forEach(init);
  initAll(document);
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-nnfa-article]').forEach(root => instances.get(root)?.());
  });
})();
